/**
 * Which recogniser a rep's dictation runs on. Pure and Angular-free so the rule
 * can be tested directly.
 *
 * In order of preference:
 * 1. The tenant's configured endpoint, when its locale serves the rep's language,
 *    whatever status the service reports for it.
 * 2. A deployed custom endpoint serving the rep's language: the rep's exact locale
 *    before a regional variant of the same language, the newest first.
 * 3. The base service at the rep's locale, where Speech supports that locale. An
 *    empty or unknown list of supported locales is treated as unknown, not as
 *    supporting nothing.
 * 4. None: dictation is unavailable in the rep's language.
 */

/** The status the Speech service reports for a custom endpoint that is deployed and serving. */
export const DEPLOYED = 'Succeeded';

/** A custom speech endpoint on the tenant's Speech resource, as the service lists it. */
export interface CustomEndpoint {
  /** The endpoint id, which the Speech SDK takes as `endpointId`. */
  id: string;
  /** BCP-47 locale the endpoint was created for. It cannot change for the life of the endpoint. */
  locale: string;
  /** Deployment status reported by the service, for example `Succeeded` or `Failed`. */
  status: string;
  /** ISO 8601 creation time. */
  createdDateTime: string;
}

/** The recogniser a dictation runs on. */
export interface RecogniserChoice {
  /** BCP-47 recognition language. */
  language: string;
  /** Custom endpoint id. Absent when the base service is used. */
  endpointId?: string;
}

/**
 * Chooses the recogniser for a rep.
 *
 * @param repLocale BCP-47 locale of the rep's language, e.g. `es-ES`.
 * @param configuredEndpointId The endpoint id in the tenant's speech configuration, if any.
 * @param endpoints Every custom endpoint on the tenant's Speech resource.
 * @param serviceLocales Locales the Speech service supports; empty or absent when unknown.
 * @returns The chosen recogniser, or `null` when none serves the rep's language or no rep locale is given.
 */
export function resolveRecogniser(
  repLocale: string | null | undefined,
  configuredEndpointId: string | null | undefined,
  endpoints: readonly CustomEndpoint[],
  serviceLocales?: readonly string[] | null,
): RecogniserChoice | null {
  const locale = repLocale?.trim();
  if (!locale) {
    return null;
  }
  const language = languageOf(locale);
  const serving = endpoints.filter((endpoint) => languageOf(endpoint.locale) === language);

  const configured = configuredEndpointId?.trim()
    ? serving.find((endpoint) => sameId(endpoint.id, configuredEndpointId))
    : undefined;
  if (configured) {
    return { language: configured.locale, endpointId: configured.id };
  }

  const [deployed] = serving
    .filter((endpoint) => endpoint.status === DEPLOYED)
    .sort((a, b) => byLocaleThenNewest(a, b, locale));
  if (deployed) {
    return { language: deployed.locale, endpointId: deployed.id };
  }

  if (!serviceLocales?.length || serviceLocales.some((supported) => sameLocale(supported, locale))) {
    return { language: locale };
  }
  return null;
}

/** The primary language subtag of a BCP-47 locale, lower-cased: `es` for `es-MX`. */
function languageOf(locale: string | null | undefined): string {
  return (locale ?? '').trim().split('-')[0].toLowerCase();
}

function sameLocale(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();
}

function sameId(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** Orders the rep's exact locale before regional variants, then newest first. */
function byLocaleThenNewest(a: CustomEndpoint, b: CustomEndpoint, locale: string): number {
  const exactA = sameLocale(a.locale, locale) ? 0 : 1;
  const exactB = sameLocale(b.locale, locale) ? 0 : 1;
  if (exactA !== exactB) {
    return exactA - exactB;
  }
  return createdAt(b) - createdAt(a);
}

/** Creation time in milliseconds; an unreadable time sorts as the oldest. */
function createdAt(endpoint: CustomEndpoint): number {
  const time = Date.parse(endpoint.createdDateTime);
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
}
